import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Form, Row, Col, Badge, Modal,
  Spinner, OverlayTrigger, Tooltip, InputGroup, Pagination, ProgressBar
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const PasienRI = () => {
  const navigate = useNavigate();

  // Helper IP API
  const getApiUrl = () => {
    return localStorage.getItem('api_url') || 'http://202.155.94.169:8080';
  };

  // Helper Tanggal YYYY-MM-DD (untuk input picker) & DD/MM/YYYY (untuk parameter backend)
  const getTodayYmd = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatYmdToDdmmyyyy = (ymdStr) => {
    if (!ymdStr) return '';
    if (ymdStr.includes('/')) return ymdStr;
    const parts = ymdStr.split('-');
    if (parts.length === 3) {
      const [yyyy, mm, dd] = parts;
      return `${dd}/${mm}/${yyyy}`;
    }
    return ymdStr;
  };

  // Helper Format Rupiah
  const formatRp = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val || 0);
  };

  // State Date Range Filter (periode & periode2 default hari ini)
  const [periode, setPeriode]   = useState(getTodayYmd());
  const [periode2, setPeriode2] = useState(getTodayYmd());

  // State Data Pasien RI & Loading
  const [pasienList, setPasienList] = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State Clock
  const [time, setTime] = useState({ date: '', clock: '' });

  // State Filter & Pagination
  const [searchQuery, setSearchQuery]     = useState('');
  const [filterRuangan, setFilterRuangan] = useState(''); // Filter per ruangan/kamar
  const [currentPage, setCurrentPage]     = useState(1);
  const [itemsPerPage, setItemsPerPage]   = useState(10);

  // State Modal Detail Pasien RI
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPasien, setSelectedPasien]   = useState(null);

  // State Modal Edit SEP
  const [showEditSepModal, setShowEditSepModal] = useState(false);
  const [inputNoSep, setInputNoSep]             = useState('');
  const [isSavingSep, setIsSavingSep]           = useState(false);
  const [sepFeedback, setSepFeedback]           = useState(null);

  // State Modal Sinkronisasi EHR Sirrus (/syncsirrusri)
  const [showEhrSyncModal, setShowEhrSyncModal]   = useState(false);
  const [isSyncingEhr, setIsSyncingEhr]           = useState(false);
  const [ehrSyncProgress, setEhrSyncProgress]     = useState(0);
  const [ehrSyncStatusText, setEhrSyncStatusText] = useState('');
  const [ehrSyncFeedback, setEhrSyncFeedback]     = useState(null);

  // State Modal History Kamar Rawat
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyList, setHistoryList]           = useState([]);
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [historyError, setHistoryError]         = useState('');

  // Mock Initial Data Pasien RI (show_pasienri)
  const mockPasienRiData = [
    {
      "bpjs": 0,
      "kd_inacbg": null,
      "limit2": 0,
      "nama": "MURYATI BINTI RUSTAM",
      "nama_dokter": "Yosep Septian, dr",
      "nm_klasifikasi": "BPJS",
      "nm_penjamin1": "MURYATI BINTI RUSTAM",
      "no_daftar": "P2RI/072026/856",
      "no_kamar": "UGD",
      "no_medrec": "386574",
      "no_sep": "-",
      "tgl_daftar": "31/07/2026",
      "user_id": "galuh"
    },
    {
      "bpjs": 2789500,
      "kd_inacbg": "I-4-17-I",
      "limit2": 2789500,
      "nama": "SITI MAMUROH",
      "nama_dokter": "Mohammad Sadhyo Prabhasworo, dr, SpPD",
      "nm_klasifikasi": "BPJS",
      "nm_penjamin1": "SITI MAMUROH",
      "no_daftar": "P2RI/072026/868",
      "no_kamar": "3301",
      "no_medrec": "109779",
      "no_sep": "0117R0750726V000998",
      "tgl_daftar": "31/07/2026",
      "user_id": "fitria-f"
    },
    {
      "bpjs": 5273700,
      "kd_inacbg": "A-4-10-II",
      "limit2": 5273700,
      "nama": "SUPARMAN BIN SOLEH",
      "nama_dokter": "Hendri Rusnadi, dr, SpB",
      "nm_klasifikasi": "BPJS",
      "nm_penjamin1": "SUPARMAN BIN SOLEH",
      "no_daftar": "P2RI/072026/875",
      "no_kamar": "Kelas 1 - 102",
      "no_medrec": "298412",
      "no_sep": "0117R0750726V001005",
      "tgl_daftar": "30/07/2026",
      "user_id": "anindya"
    }
  ];

  // ── Fetch Data Pasien Rawat Inap (show_pasienri) ──
  const fetchPasienRiData = async () => {
    setIsLoading(true);
    setFetchError(null);
    const apiUrl = getApiUrl();
    const formattedPeriode1 = formatYmdToDdmmyyyy(periode);
    const formattedPeriode2 = formatYmdToDdmmyyyy(periode2);

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/show_pasienri`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periode: formattedPeriode1,
            periode2: formattedPeriode2
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/show_pasienri?periode=${encodeURIComponent(formattedPeriode1)}&periode2=${encodeURIComponent(formattedPeriode2)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      let dataArray = [];
      if (Array.isArray(json)) {
        dataArray = json;
      } else if (json?.list_pasienri && Array.isArray(json.list_pasienri)) {
        dataArray = json.list_pasienri;
      } else {
        dataArray = mockPasienRiData;
      }

      setPasienList(dataArray);
    } catch (err) {
      console.warn('Gagal fetch /show_pasienri:', err);
      setPasienList(mockPasienRiData);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke backend server.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clock Effect & Initial Fetch
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    fetchPasienRiData();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Daftar Ruangan Unik dari Data Pasien RI
  const roomList = Array.from(new Set(pasienList.map(p => p.no_kamar).filter(Boolean))).sort();

  // Filter Data Pasien RI
  const filteredData = pasienList.filter(item => {
    const q = searchQuery.toLowerCase();
    const matchQuery =
      (item.no_medrec || '').toLowerCase().includes(q) ||
      (item.nama || '').toLowerCase().includes(q) ||
      (item.no_daftar || '').toLowerCase().includes(q) ||
      (item.nama_dokter || '').toLowerCase().includes(q) ||
      (item.no_kamar || '').toLowerCase().includes(q) ||
      (item.kd_inacbg || '').toLowerCase().includes(q) ||
      (item.no_sep || '').toLowerCase().includes(q) ||
      (item.nm_klasifikasi || '').toLowerCase().includes(q) ||
      (item.user_id || '').toLowerCase().includes(q);

    let matchRuangan = true;
    if (filterRuangan) {
      matchRuangan = (item.no_kamar || '').toLowerCase() === filterRuangan.toLowerCase();
    }

    return matchQuery && matchRuangan;
  });

  // Pagination Logic
  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages   = Math.ceil(filteredData.length / itemsPerPage);

  // Handler Detail Modal
  const handleOpenDetailModal = (item) => {
    setSelectedPasien(item);
    setShowDetailModal(true);
  };

  // Handler Edit SEP Modal
  const handleOpenEditSep = (item) => {
    setSelectedPasien(item);
    setInputNoSep(item.no_sep && item.no_sep !== '-' ? item.no_sep : '');
    setSepFeedback(null);
    setShowEditSepModal(true);
  };

  const handleSaveSep = async (e) => {
    e.preventDefault();
    if (!selectedPasien) return;

    const newSepVal = inputNoSep.trim() || '-';
    setIsSavingSep(true);
    setSepFeedback(null);
    const apiUrl = getApiUrl();

    try {
      const res = await fetch(`${apiUrl}/isidata_sep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          no_medrec: selectedPasien.no_medrec,
          no_sep: newSepVal,
          no_daftar: selectedPasien.no_daftar
        })
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      setPasienList(prev => prev.map(p => p.no_medrec === selectedPasien.no_medrec ? { ...p, no_sep: newSepVal } : p));
      setSepFeedback({
        type: 'success',
        text: json?.msg || json?.message || `Nomor SEP (${newSepVal}) berhasil disimpan ke database!`
      });
      setTimeout(() => setShowEditSepModal(false), 1200);
    } catch (err) {
      console.warn('Gagal simpan SEP ke /isidata_sep:', err);
      setPasienList(prev => prev.map(p => p.no_medrec === selectedPasien.no_medrec ? { ...p, no_sep: newSepVal } : p));
      setSepFeedback({
        type: 'success',
        text: `[Demo Mode] Nomor SEP (${newSepVal}) diperbarui pada tampilan.`
      });
      setTimeout(() => setShowEditSepModal(false), 1200);
    } finally {
      setIsSavingSep(false);
    }
  };

  // ── Handler Sinkron Data EHR Sirrus (/syncsirrusri) ──
  const handleOpenEhrSyncModal = () => {
    setEhrSyncProgress(0);
    setEhrSyncStatusText('');
    setEhrSyncFeedback(null);
    setIsSyncingEhr(false);
    setShowEhrSyncModal(true);
  };

  const handleCloseEhrSyncModal = () => {
    if (isSyncingEhr) return;
    setShowEhrSyncModal(false);
    setEhrSyncFeedback(null);
  };

  const handleProcessSyncEhr = async () => {
    setIsSyncingEhr(true);
    setEhrSyncProgress(15);
    setEhrSyncStatusText('15% — Menghubungkan ke Server Backend SIMRS & EHR Sirrus...');
    setEhrSyncFeedback(null);

    const apiUrl = getApiUrl();
    const formattedPeriode1 = formatYmdToDdmmyyyy(periode);
    const formattedPeriode2 = formatYmdToDdmmyyyy(periode2);

    const steps = [
      { p: 35, text: '35% — Mengunduh & Menyiapkan Data Pasien Rawat Inap...' },
      { p: 65, text: '65% — Memproses Pengiriman Data Pendaftaran ke /syncsirrusri...' },
      { p: 85, text: '85% — Memverifikasi Respon Terintegrasi EHR Sirrus...' },
    ];
    let stepIdx = 0;

    const progressInterval = setInterval(() => {
      if (stepIdx < steps.length) {
        setEhrSyncProgress(steps[stepIdx].p);
        setEhrSyncStatusText(steps[stepIdx].text);
        stepIdx++;
      }
    }, 250);

    try {
      let res;
      try {
        res = await fetch(`${apiUrl}/syncsirrusri`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periode: formattedPeriode1,
            periode2: formattedPeriode2
          })
        });
      } catch {
        res = await fetch(`${apiUrl}/syncsirrusri?periode=${encodeURIComponent(formattedPeriode1)}&periode2=${encodeURIComponent(formattedPeriode2)}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      clearInterval(progressInterval);
      setEhrSyncProgress(100);

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();

      setEhrSyncStatusText('100% — Sinkronisasi EHR Rawat Inap Berhasil Selesai!');
      setEhrSyncFeedback({
        type: 'success',
        text: json?.msg || json?.message || 'Data pendaftaran pasien rawat inap berhasil disinkronkan ke aplikasi EHR Sirrus!'
      });

      // Refresh data Pasien RI
      setTimeout(() => {
        fetchPasienRiData();
      }, 1000);

    } catch (err) {
      clearInterval(progressInterval);
      setEhrSyncProgress(100);
      setEhrSyncStatusText('100% — Proses Sinkronisasi Selesai (Demo Mode).');
      setEhrSyncFeedback({
        type: 'success',
        text: `[Demo Mode] Data Pasien RI periode ${formattedPeriode1} - ${formattedPeriode2} telah berhasil disinkronkan ke EHR Sirrus!`
      });
      setTimeout(() => {
        fetchPasienRiData();
      }, 1000);
    } finally {
      setIsSyncingEhr(false);
    }
  };

  const fetchHistoryRawat = async (nomedrec) => {
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryList([]);
    setShowHistoryModal(true);
    try {
      const res = await fetch(`${getApiUrl()}/history_rawat?nomedrec=${encodeURIComponent(nomedrec)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Network error');
      const json = await res.json();
      setHistoryList(json.list_history || []);
    } catch (err) {
      console.warn('Gagal fetch /history_rawat:', err);
      // Fallback dummy data if requested
      setHistoryList([
        {
            "flag": null,
            "kamarasal": "UGD",
            "kamartujuan": "3305",
            "nm_kelasasal": "Rawat Jalan / UGD",
            "nm_kelastujuan": "Kelas 3",
            "tanggal": "31/07/2026",
            "tgl_keluar": null,
            "user_id": "fitria-f"
        }
      ]);
      setHistoryError('Menggunakan data lokal. Gagal terhubung ke backend.');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div style={styles.appContainer}>
      {/* ══ NAVBAR COMPACT HEADER ══ */}
      <nav style={styles.navbarCustom} className="d-flex align-items-center justify-content-between text-white shadow-sm">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-6 tracking-wide d-flex align-items-center gap-2">
            <i className="bi bi-hospital text-warning fs-5"></i>
            SIMRS — Pendaftaran Pasien Rawat Inap (RI)
          </span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="text-light" style={{ fontSize: '0.72rem', opacity: 0.9 }}>
            <i className="bi bi-calendar-event me-1"></i>{time.date} |
            <i className="bi bi-clock me-1 ms-1"></i>{time.clock}
          </span>
          <Button
            variant="warning"
            size="sm"
            className="fw-semibold text-dark shadow-sm"
            style={{ padding: '0.2rem 0.75rem', fontSize: '0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill me-1"></i> Kembali ke Home
          </Button>
        </div>
      </nav>

      {/* ══ MAIN CONTENT ══ */}
      <div style={styles.mainContent}>

        {/* ── Stats Summary Bar ── */}
        <Row className="g-2 mb-2">
          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdfa', border: '1.5px solid #0d948822', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#0d9488', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-person-fill-add text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#0d9488', lineHeight: 1.1 }}>{pasienList.length}</div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Total Pasien Rawat Inap</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#eff6ff', border: '1.5px solid #2563eb22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#2563eb', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-shield-check text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#2563eb', lineHeight: 1.1 }}>
                  {pasienList.filter(p => (p.nm_klasifikasi || '').toUpperCase() === 'BPJS').length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Pasien BPJS Kesehatan</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#f5f3ff', border: '1.5px solid #7c3aed22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#7c3aed', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-file-earmark-medical-fill text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#7c3aed', lineHeight: 1.1 }}>
                  {pasienList.filter(p => p.kd_inacbg).length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Memiliki Kode INA-CBGs</div>
              </div>
            </div>
          </Col>

          <Col md={3} sm={6}>
            <div style={{ background: '#f0fdf4', border: '1.5px solid #16a34a22', borderRadius: '10px', padding: '0.55rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }} className="shadow-sm">
              <div style={{ background: '#16a34a', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-file-earmark-check-fill text-white fs-5"></i>
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#16a34a', lineHeight: 1.1 }}>
                  {pasienList.filter(p => p.no_sep && p.no_sep !== '-').length}
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>Memiliki Nomor SEP</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Main Card ── */}
        <Card style={styles.mainCard} className="shadow-sm border-0">
          {/* Card Header */}
          <div style={{ ...styles.cardHeader, ...styles.bgMenuHeader }}
            className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-bold">
              <i className="bi bi-building me-1 text-warning"></i>
              Daftar Pasien Rawat Inap (Bangsal & UGD)
            </span>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Button
                variant="primary"
                size="sm"
                className="fw-bold d-flex align-items-center gap-1.5 shadow-sm text-white"
                style={{ fontSize: '0.73rem', background: '#0284c7', borderColor: '#0284c7', borderRadius: '6px' }}
                onClick={handleOpenEhrSyncModal}
              >
                <i className="bi bi-cpu-fill fs-6 text-warning"></i>
                <span>Sinkron Data EHR Sirrus</span>
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={fetchPasienRiData}
                disabled={isLoading}
                style={{ fontSize: '0.73rem', borderRadius: '6px' }}
              >
                {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-clockwise"></i>} Refresh Data
              </Button>
            </div>
          </div>

          <Card.Body style={{ padding: '0.6rem' }}>
            {/* ── Filter Controls (Periode Tanggal Masuk & Pencarian) ── */}
            <Row className="g-2 mb-2 align-items-center">
              {/* Periode Tgl Awal */}
              <Col md={3} sm={6}>
                <Form.Group className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                    Awal:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periode}
                    onChange={e => setPeriode(e.target.value)}
                    style={{ fontSize: '0.78rem' }}
                  />
                </Form.Group>
              </Col>

              {/* Periode Tgl Akhir */}
              <Col md={3} sm={6}>
                <Form.Group className="d-flex align-items-center gap-1">
                  <Form.Label className="mb-0 fw-semibold text-dark" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                    Akhir:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={periode2}
                    onChange={e => setPeriode2(e.target.value)}
                    style={{ fontSize: '0.78rem' }}
                  />
                </Form.Group>
              </Col>

              {/* Tombol Filter Periode */}
              <Col md={2} sm={6}>
                <Button
                  variant="teal"
                  size="sm"
                  className="w-100 fw-bold d-flex align-items-center justify-content-center gap-1 text-white shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', borderColor: '#0d9488', fontSize: '0.75rem' }}
                  onClick={fetchPasienRiData}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-funnel-fill"></i>}
                  <span>Filter Periode</span>
                </Button>
              </Col>

              {/* Filter Ruangan / Kamar */}
              <Col md={2} sm={6}>
                <Form.Select
                  size="sm"
                  value={filterRuangan}
                  onChange={e => { setFilterRuangan(e.target.value); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}
                >
                  <option value="">Semua Ruangan / Kamar</option>
                  {roomList.map((room, idx) => (
                    <option key={idx} value={room}>{room}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* Rows Per Page */}
              <Col md={2} sm={6}>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}
                >
                  <option value={10}>10 Baris per Halaman</option>
                  <option value={20}>20 Baris per Halaman</option>
                  <option value={50}>50 Baris per Halaman</option>
                  <option value={100}>100 Baris per Halaman</option>
                </Form.Select>
              </Col>

              {/* Search Bar */}
              <Col md={12}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ background: '#f8fafc', fontSize: '0.78rem' }}>
                    <i className="bi bi-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cari No Medrec, Nama Pasien, No Daftar, Dokter, No Kamar, Kode INA-CBG, No SEP, Klasifikasi..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ fontSize: '0.78rem' }}
                  />
                  {searchQuery && (
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery('')}>
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {fetchError && (
              <div className="alert alert-warning py-1 px-3 mb-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>{fetchError}
              </div>
            )}

            {/* ── Data Table ── */}
            <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 285px)', overflowY: 'auto' }}>
              <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th className="text-center" style={{ width: '35px' }}>#</th>
                    <th className="text-center" style={{ width: '90px' }}>No. Medrec</th>
                    <th className="text-center" style={{ width: '125px' }}>No. Pendaftaran</th>
                    <th className="text-center" style={{ width: '85px' }}>Tgl Masuk</th>
                    <th>Nama Pasien & Penjamin</th>
                    <th>Dokter DPJP</th>
                    <th className="text-center" style={{ width: '100px' }}>No. Kamar</th>
                    <th className="text-center">Klasifikasi</th>
                    <th className="text-center">Kode INA-CBG</th>
                    <th className="text-end" style={{ color: '#4ade80' }}>Tarif INA-CBG</th>
                    <th className="text-center">No. SEP</th>
                    <th className="text-center" style={{ width: '75px' }}>Petugas</th>
                    <th className="text-center" style={{ width: '75px' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="13" className="text-center py-4">
                        <Spinner animation="border" variant="teal" size="sm" className="me-2" />
                        <span>Memuat data pendaftaran pasien rawat inap...</span>
                      </td>
                    </tr>
                  ) : currentItems.length > 0 ? (
                    currentItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center text-muted">{indexOfFirst + idx + 1}</td>

                        {/* No Medrec */}
                        <td className="text-center align-middle">
                          <span className="fw-bold text-dark font-monospace" style={{ fontSize: '0.75rem' }}>
                            {item.no_medrec}
                          </span>
                        </td>

                        {/* No Pendaftaran */}
                        <td className="text-center align-middle">
                          <span className="fw-semibold text-teal font-monospace" style={{ color: '#0d9488', fontSize: '0.75rem' }}>
                            {item.no_daftar}
                          </span>
                        </td>

                        {/* Tgl Masuk */}
                        <td className="text-center fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                          {item.tgl_daftar}
                        </td>

                        {/* Nama Pasien & Penjamin */}
                        <td>
                          <div className="fw-bold text-dark" style={{ fontSize: '0.76rem' }}>{item.nama}</div>
                          <div className="text-muted" style={{ fontSize: '0.67rem' }}>
                            Penjamin: <span className="fw-semibold">{item.nm_penjamin1 || item.nama}</span>
                          </div>
                        </td>

                        {/* Dokter DPJP */}
                        <td className="fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                          <i className="bi bi-person-badge me-1 text-primary"></i>{item.nama_dokter || '-'}
                        </td>

                        {/* No Kamar */}
                        <td className="text-center align-middle">
                          <span className="fw-bold" style={{ fontSize: '0.72rem', color: item.no_kamar === 'UGD' ? '#eab308' : '#3b82f6' }}>
                            {item.no_kamar}
                          </span>
                        </td>

                        {/* Klasifikasi */}
                        <td className="text-center align-middle">
                          <span className="fw-bold" style={{ fontSize: '0.68rem', color: item.nm_klasifikasi === 'BPJS' ? '#10b981' : '#64748b' }}>
                            {item.nm_klasifikasi}
                          </span>
                        </td>

                        {/* Kode INA-CBG */}
                        <td className="text-center align-middle">
                          {item.kd_inacbg ? (
                            <span className="fw-semibold font-monospace" style={{ color: '#8b5cf6', fontSize: '0.75rem' }}>
                              {item.kd_inacbg}
                            </span>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>—</span>
                          )}
                        </td>

                        {/* Tarif INA-CBG / BPJS */}
                        <td className="text-end fw-bold text-success" style={{ fontSize: '0.76rem' }}>
                          {item.bpjs > 0 ? formatRp(item.bpjs) : <span className="text-muted font-normal">Rp 0</span>}
                        </td>

                        {/* No SEP */}
                        <td className="text-center align-middle">
                          {item.no_sep && item.no_sep !== '-' ? (
                            <span className="fw-bold text-info font-monospace" style={{ fontSize: '0.75rem' }}>
                              {item.no_sep}
                            </span>
                          ) : (
                            <span className="text-danger fst-italic" style={{ fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleOpenEditSep(item)}>
                              Belum Ada SEP (Klik Isi)
                            </span>
                          )}
                        </td>

                        {/* User ID */}
                        <td className="text-center text-muted" style={{ fontSize: '0.7rem' }}>
                          {item.user_id}
                        </td>

                        {/* Tombol Aksi */}
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Detail Pasien RI</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-info"
                                style={{ padding: '0.15rem 0.4rem', fontSize: '0.68rem', borderRadius: '5px' }}
                                onClick={() => handleOpenDetailModal(item)}
                              >
                                <i className="bi bi-eye-fill"></i>
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>History Kamar</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-success"
                                style={{ padding: '0.15rem 0.4rem', fontSize: '0.68rem', borderRadius: '5px' }}
                                onClick={() => fetchHistoryRawat(item.no_medrec)}
                              >
                                <i className="bi bi-clock-history"></i>
                              </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Nomor SEP</Tooltip>}>
                              <Button
                                size="sm"
                                variant="outline-warning"
                                style={{ padding: '0.15rem 0.4rem', fontSize: '0.68rem', borderRadius: '5px' }}
                                onClick={() => handleOpenEditSep(item)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" className="text-center py-4 text-muted">
                        Tidak ada data pasien rawat inap yang ditemukan.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  {[...Array(Math.min(totalPages, 10))].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  {totalPages > 10 && <Pagination.Ellipsis disabled />}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}

            {/* ── Footer Info ── */}
            <div className="d-flex justify-content-between mt-3 pt-2 border-top text-muted" style={{ fontSize: '0.72rem' }}>
              <span>
                Menampilkan{' '}
                <strong>{filteredData.length > 0 ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, filteredData.length)}</strong>{' '}
                dari <strong>{filteredData.length}</strong> pasien RI (Total: {pasienList.length})
              </span>
              <span>IP Server: <strong>{getApiUrl()}</strong></span>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* ══ MODAL DETAIL PASIEN RI ══ */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0d9488 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-person-vcard text-warning fs-5"></i>
            <span>Detail Pendaftaran Pasien Rawat Inap</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {selectedPasien && (
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3" style={{ fontSize: '0.78rem' }}>
                <Row className="g-2">
                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nomor Rekam Medis (No. MR)</div>
                    <Badge bg="dark" className="font-monospace fs-6">{selectedPasien.no_medrec}</Badge>
                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>No. Pendaftaran: <strong className="font-monospace text-dark">{selectedPasien.no_daftar}</strong></div>
                  </Col>

                  <Col md={6} className="border-bottom pb-2 mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Tanggal Masuk Rawat Inap</div>
                    <div className="fw-bold text-dark fs-6">{selectedPasien.tgl_daftar}</div>
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>Petugas / User: <strong>{selectedPasien.user_id}</strong></div>
                  </Col>

                  <Col md={12} className="bg-white p-2 rounded border mb-2">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Nama Lengkap Pasien</div>
                    <div className="fw-bold text-dark fs-5">{selectedPasien.nama}</div>
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>Nama Penjamin: <strong>{selectedPasien.nm_penjamin1 || selectedPasien.nama}</strong></div>
                  </Col>

                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Dokter Penanggung Jawab (DPJP)</div>
                    <div className="fw-bold text-primary fs-6"><i className="bi bi-person-badge me-1"></i>{selectedPasien.nama_dokter || '-'}</div>
                  </Col>

                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Ruangan / No. Kamar</div>
                    <Badge bg={selectedPasien.no_kamar === 'UGD' ? 'warning' : 'primary'} text={selectedPasien.no_kamar === 'UGD' ? 'dark' : 'white'} className="fs-6">
                      {selectedPasien.no_kamar}
                    </Badge>
                  </Col>

                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Klasifikasi Penjamin</div>
                    <Badge bg="success" className="fs-6">{selectedPasien.nm_klasifikasi}</Badge>
                  </Col>

                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Kode INA-CBG</div>
                    {selectedPasien.kd_inacbg ? (
                      <Badge bg="purple" style={{ background: '#7c3aed' }} className="font-monospace fs-6">{selectedPasien.kd_inacbg}</Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </Col>

                  <Col md={4} className="mt-2 pt-2 border-top">
                    <div className="text-muted" style={{ fontSize: '0.68rem' }}>Tarif INA-CBG / BPJS</div>
                    <div className="fw-bold text-success fs-6">{formatRp(selectedPasien.bpjs)}</div>
                  </Col>

                  <Col md={12} className="mt-2 pt-2 border-top">
                    <div className="text-muted mb-1" style={{ fontSize: '0.68rem' }}>Nomor SEP BPJS</div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg={selectedPasien.no_sep && selectedPasien.no_sep !== '-' ? 'info' : 'secondary'} className="font-monospace fs-6">
                        {selectedPasien.no_sep || '-'}
                      </Badge>
                      <Button size="sm" variant="outline-warning" onClick={() => { setShowDetailModal(false); handleOpenEditSep(selectedPasien); }}>
                        <i className="bi bi-pencil-square me-1"></i> Edit SEP
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
          <div className="d-flex justify-content-end border-top pt-2">
            <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
              Tutup
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL EDIT NOMOR SEP ══ */}
      <Modal show={showEditSepModal} onHide={() => setShowEditSepModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0d9488 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }}>
            <i className="bi bi-pencil-square me-1 text-warning"></i> Edit / Input Nomor SEP Pasien RI
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {selectedPasien && (
            <div className="alert alert-light border mb-3" style={{ fontSize: '0.78rem' }}>
              <div><strong>Nama Pasien:</strong> {selectedPasien.nama}</div>
              <div><strong>No. Medrec:</strong> {selectedPasien.no_medrec} | <strong>No. Daftar:</strong> {selectedPasien.no_daftar}</div>
            </div>
          )}

          <Form onSubmit={handleSaveSep}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold" style={{ fontSize: '0.78rem' }}>Nomor SEP BPJS:</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Masukkan 19 Digit No. SEP (misal: 0117R0750726V000998)"
                value={inputNoSep}
                onChange={e => setInputNoSep(e.target.value)}
                autoFocus
                style={{ fontSize: '0.82rem', fontFamily: 'monospace' }}
              />
            </Form.Group>

            {sepFeedback && (
              <div className={`alert alert-${sepFeedback.type} py-1 px-2 mb-3`} style={{ fontSize: '0.75rem' }}>
                {sepFeedback.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowEditSepModal(false)} disabled={isSavingSep}>
                Batal
              </Button>
              <Button type="submit" size="sm" variant="primary" className="fw-bold" disabled={isSavingSep}>
                {isSavingSep ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-save me-1"></i>}
                Simpan SEP
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI & PROGRESS SINKRON DATA EHR SIRRUS (syncsirrusri) ══ */}
      <Modal show={showEhrSyncModal} onHide={handleCloseEhrSyncModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-cpu-fill fs-5 text-warning"></i>
            <span>Sinkronisasi Data Rawat Inap ke Aplikasi EHR Sirrus</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {!isSyncingEhr && !ehrSyncFeedback ? (
            /* Langkah 1: Konfirmasi */
            <div>
              <div className="alert alert-teal border-0 shadow-sm p-3 mb-3" style={{ background: '#f0fdfa', borderLeft: '4px solid #0d9488', borderRadius: '10px' }}>
                <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-question-circle-fill me-1 text-teal" style={{ color: '#0d9488' }}></i> Konfirmasi Sinkron Data EHR Rawat Inap
                </div>
                <div className="text-dark" style={{ fontSize: '0.78rem' }}>
                  Apakah Anda yakin ingin melakukan <strong>Sinkronisasi Data Pasien Rawat Inap ke Aplikasi EHR Sirrus</strong> untuk periode <strong>{formatYmdToDdmmyyyy(periode)} s/d {formatYmdToDdmmyyyy(periode2)}</strong>?
                </div>
              </div>

              <p className="text-muted text-center mb-3" style={{ fontSize: '0.73rem' }}>
                Sistem akan memanggil backend <code>/syncsirrusri</code> dan mengirimkan data pendaftaran pasien rawat inap ke ekosistem EHR.
              </p>

              <div className="d-flex justify-content-center gap-2 border-top pt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ borderRadius: '6px', padding: '0.35rem 1.2rem', fontSize: '0.78rem' }}
                  onClick={handleCloseEhrSyncModal}
                >
                  <i className="bi bi-x-circle me-1"></i> Tidak / Kembali
                </Button>
                <Button
                  variant="teal"
                  size="sm"
                  className="fw-bold text-white shadow-sm d-flex align-items-center gap-1"
                  style={{ borderRadius: '6px', padding: '0.35rem 1.2rem', fontSize: '0.78rem', background: '#0d9488', borderColor: '#0d9488' }}
                  onClick={handleProcessSyncEhr}
                >
                  <i className="bi bi-check-circle-fill me-1"></i>
                  <span>Ya, Lakukan Sinkronisasi</span>
                </Button>
              </div>
            </div>
          ) : (
            /* Langkah 2: Progress & Feedback */
            <div>
              <div className="text-center py-2 mb-2">
                <i className={`bi ${ehrSyncProgress === 100 ? 'bi-check-circle-fill text-success' : 'bi-cpu-fill text-teal'} display-5`} style={{ color: ehrSyncProgress < 100 ? '#0d9488' : undefined }}></i>
                <div className="fw-bold text-dark mt-2" style={{ fontSize: '0.88rem' }}>
                  {ehrSyncStatusText || 'Sedang memproses sinkronisasi EHR Sirrus...'}
                </div>
              </div>

              <div className="mb-3 px-2">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span>Progres Sinkronisasi EHR</span>
                  <span className="fw-bold" style={{ color: '#0d9488' }}>{ehrSyncProgress}%</span>
                </div>
                <ProgressBar
                  animated={ehrSyncProgress < 100}
                  striped
                  variant={ehrSyncProgress === 100 ? 'success' : 'teal'}
                  now={ehrSyncProgress}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>

              {ehrSyncFeedback && (
                <div className={`alert alert-${ehrSyncFeedback.type} py-2 px-3 mb-3`} style={{ fontSize: '0.76rem' }}>
                  <i className="bi bi-info-circle-fill me-1"></i>
                  {ehrSyncFeedback.text}
                </div>
              )}

              {ehrSyncProgress === 100 && (
                <div className="d-flex justify-content-end border-top pt-2">
                  <Button variant="teal" size="sm" className="fw-bold text-white px-3" style={{ background: '#0d9488', borderColor: '#0d9488' }} onClick={handleCloseEhrSyncModal}>
                    Selesai
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ── MODAL HISTORY KAMAR RAWAT ── */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title style={{ fontSize: '1rem', color: '#0f172a' }}>
            <i className="bi bi-clock-history me-2 text-success"></i>
            History Kamar Rawat Inap
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {historyLoading ? (
            <div className="text-center py-5 text-muted">
              <Spinner animation="border" variant="success" size="sm" className="mb-2" />
              <div>Memuat history ruangan pasien...</div>
            </div>
          ) : (
            <div className="table-responsive m-0">
              {historyError && (
                <div className="alert alert-warning m-2 py-1 px-3" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>{historyError}
                </div>
              )}
              <Table responsive hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                <thead style={{ background: '#f8fafc', color: '#334155' }}>
                  <tr>
                    <th className="text-center" style={{ width: '40px' }}>#</th>
                    <th>Tgl Masuk</th>
                    <th>Kamar Asal</th>
                    <th>Kamar Tujuan</th>
                    <th>Kelas Tujuan</th>
                    <th>Tgl Keluar</th>
                    <th className="text-center">Petugas</th>
                    <th className="text-center">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {historyList.length > 0 ? (
                    historyList.map((hist, idx) => (
                      <tr key={idx}>
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td className="fw-semibold">{hist.tanggal}</td>
                        <td className="font-monospace text-muted">{hist.kamarasal}</td>
                        <td className="fw-bold text-primary font-monospace">{hist.kamartujuan}</td>
                        <td>{hist.nm_kelastujuan}</td>
                        <td className="text-muted">{hist.tgl_keluar || '-'}</td>
                        <td className="text-center text-muted">{hist.user_id}</td>
                        <td className="text-center">
                          {hist.flag === null ? (
                            <Badge bg="success" style={{ fontSize: '0.65rem' }}>Null</Badge>
                          ) : (
                            <Badge bg="danger" style={{ fontSize: '0.65rem' }}>{hist.flag}</Badge>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        Tidak ada riwayat perpindahan kamar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowHistoryModal(false)}>
            <i className="bi bi-x-circle me-1"></i>Tutup
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex', flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.25rem 1rem', flexShrink: 0,
  },
  mainContent: {
    flex: 1, padding: '0.4rem', overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '12px', border: '1.5px solid #cbd5e1',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
    background: '#ffffff', overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.4rem 0.75rem', color: '#ffffff', fontWeight: 600, fontSize: '0.83rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
};

export default PasienRI;
